import React, { useRef, useState } from 'react'
import cx from 'classnames'
import { useAppSelector } from 'reduxTyped'
import { AvatarRound, l, List, MainNavigation } from '@manychat/manyui'

import { UpgradeSource } from 'common/billing'
import GoProBtn from 'common/billing/components/GoProBtn'
import ContactsLimit from 'common/core/components/ContactsLimit'
import HelpPopover from 'common/core/components/HelpPopover/HelpPopover'
import MyProfilePopover from 'common/core/components/MyProfilePopover/MyProfilePopover'
import { getProfileDashboardStatus, getUser } from 'common/core/selectors/appSelectors'
import { IfHasPermission, PermissionTargets } from 'common/userRoles'
import { getProfileImageStubURL } from 'utils/assets'

import { MyProfileLinkPopover } from '../Sidebar/MyProfileLink/MyProfileLinkPopover/MyProfileLinkPopover'

import { useNavParams } from './useNavParams'

export const BottomContent = () => {
  const [isMyProfileOpen, setIsMyProfileOpen] = useState(false)
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const myProfileButtonRef = useRef<HTMLButtonElement>(null)
  const helpButtonRef = useRef<HTMLButtonElement>(null)

  const { pageId, isPRO, isContactsLimitBanner, isRetryPayment, hasTrial, isSidebarHidden } =
    useNavParams()

  const profile = useAppSelector(getUser)
  const hasProfileDashboard = useAppSelector(getProfileDashboardStatus)

  return (
    <>
      <List className="p-a-xs d-flex flex-col">
        <MainNavigation.Item
          as="button"
          icon={
            <AvatarRound
              size="xsmall"
              src={profile?.avatar_small}
              placeholderSrc={getProfileImageStubURL()}
              alt="My profile avatar"
            />
          }
          ariaLabel={l.translate('My Profile')}
          tooltip={l.translate('My Profile')}
          collapsed={isSidebarHidden}
          ref={myProfileButtonRef}
          onClick={() => setIsMyProfileOpen((prev) => !prev)}
          data-test-id="profile-popover"
        >
          {l.translate('My Profile')}
        </MainNavigation.Item>
        <MainNavigation.Item
          icon="Question"
          ariaLabel={l.translate('Help')}
          tooltip={l.translate('Help')}
          collapsed={isSidebarHidden}
          as="button"
          onClick={() => setIsHelpOpen((prev) => !prev)}
          ref={helpButtonRef}
          data-test-id="help-popover"
        >
          {l.translate('Help')}
        </MainNavigation.Item>
        {hasProfileDashboard ? (
          <MyProfileLinkPopover
            isOpen={isMyProfileOpen}
            anchor={myProfileButtonRef.current || null}
            onClose={() => setIsMyProfileOpen(false)}
          />
        ) : (
          <MyProfilePopover
            anchor={myProfileButtonRef.current || null}
            open={isMyProfileOpen}
            onRequestClose={() => setIsMyProfileOpen(false)}
          />
        )}
        <HelpPopover
          open={isHelpOpen}
          anchor={helpButtonRef.current || undefined}
          pageId={pageId}
          onRequestClose={() => setIsHelpOpen(false)}
          useLayerForClickAway
        />
      </List>
      {!isPRO && (
        <div className={cx({ 'b-t': isContactsLimitBanner })}>
          {isContactsLimitBanner && (
            <ContactsLimit
              isSidebarHidden={isSidebarHidden}
              fbPagesOpen={!isSidebarHidden}
              isRightTimeAnimate={false}
            />
          )}
          <IfHasPermission target={PermissionTargets.SETTINGS_BILLING}>
            {!isRetryPayment ? (
              <div className={cx('m-b-sm', isSidebarHidden ? 'm-x-xxs' : 'm-x')}>
                <GoProBtn
                  request={{
                    source: UpgradeSource.SIDEBAR,
                  }}
                  label={isSidebarHidden ? 'PRO' : undefined}
                  size="small"
                  className={cx(isSidebarHidden && 'p-x-xxs')}
                  variant={hasTrial ? 'success' : 'primary'}
                  fullWidth
                  isSidebarHidden={isSidebarHidden}
                />
              </div>
            ) : null}
          </IfHasPermission>
        </div>
      )}
    </>
  )
}
